<script setup lang="ts">
const $props = defineProps<{
	content: string | Ref | null
	title: string | Ref | null
	closeText?: string | null
}>()

const emits = defineEmits(['close'])
const modal = ref<HTMLElement | null>(null)
const formattedTitle = computed<string>(() => {
	if (
		$props.title === null
	) {
		return ''
	}

	if (typeof $props.title === 'string') {
		return $props.title
	}

	if (
		$props.title?.value === undefined
		|| $props.title?.value === null
	) {
		return ''
	}

	return $props.title?.value
})

const formattedContent = computed<string>(() => {
	if (
		$props.content === null
	) {
		return ''
	}

	if (typeof $props.content === 'string') {
		return $props.content
	}

	if (
		$props.content?.value === undefined
		|| $props.content?.value === null
	) {
		return ''
	}

	return $props.content?.value
})

const contentContainer = ref<HTMLElement | null>(null)
function setLinksToNewTab() {
	if (!contentContainer.value) {
		return
	}

	const links = contentContainer.value.querySelectorAll('a')
	if (links.length === 0) {
		return
	}

	links.forEach((link) => {
		link.setAttribute('target', '_blank')
		link.setAttribute('rel', 'noopener noreferrer')
	})
}

onMounted(() => {
	nextTick(() => setLinksToNewTab())
})

watch(formattedContent, () => {
	nextTick(() => setLinksToNewTab())
})

onClickOutside(modal, (event) => {
	// Don't close if clicking a link inside the modal content
	if (event.target instanceof HTMLAnchorElement) {
		return
	}
	emits('close')
})
</script>

<template>
	<div
		class="relative z-10"
		aria-labelledby="modal-title"
		role="dialog"
		aria-modal="true"
	>
		<div class="fixed inset-0 bg-gray-500/75 transition-opacity" />

		<div class="fixed inset-0 z-10 overflow-y-auto">
			<div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
				<div ref="modal" class="relative overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
					<div class="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
						<div class="sm:flex sm:items-start">
							<div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
								<h3 v-if="formattedTitle" id="modal-title" class="text-base font-semibold leading-6 text-gray-900">
									{{ formattedTitle }}
								</h3>
								<div
									v-if="formattedContent"
									ref="contentContainer"
									class="prose mt-2 max-w-none text-sm"
									v-html="formattedContent"
								/>
							</div>
						</div>
					</div>
					<div class="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
						<button-base
							type="button"
							variant="secondary"
							class="w-full sm:w-auto"
							@click.self="emits('close')"
						>
							{{ closeText ?? t('app.close') }}
						</button-base>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>
